// How likely each point is to lie on a plane, 0 to 1, at any slant: the shape of its neighbourhood (every shown
// cloud's points within r, scans overlap) by local PCA, so a floor, a wall and a tilted board score alike. Each point
// is read on its own, from the neighbours facing its way (normals within deg of its own), so a floor point beside a
// wall reads the floor alone: where planes meet, each point keeps its own plane's score. A point scores 1 when those
// neighbours lie thin across (to within tol), reach out from it (not a small curved patch: a ball's points facing one
// way cover only a cap of it) and spread both ways (not a rod), the points lying on that plane mostly face its way
// (clutter's face every way), their normals and its own agree with the plane (each
// frame's normals hold up where frames sit a little apart and a plane looks thick), and it sits on it; a curve,
// clutter or a lone point scores low. Points without normals are read by their positions alone, every neighbour
// counting. Pure, for the Plane detector step and the data steps' planar source.
import { eigSym3 } from './select.ts';

/** The fewest neighbours a point's shape is read from: fewer, it is unknown (NaN). */
const MIN_PTS = 6;
/** The neighbours are thinned to a point per cube this fraction of r (some 30 to 80 on a plane), so dense scans cost what sparse ones do. */
const THIN = 1 / 4;
/** A point takes the fits of the pool's points within this fraction of r facing its way, nearer ones counting more (no cube edges show). */
const BLEND = 0.375;

export interface PlanarCloud {
  P: Float32Array;
  /** Unit normals (NaN: none), or none at all. */
  N?: Float32Array | null;
  /** Points that are neighbours (shown and live); null: none of this cloud. */
  pool: Uint8Array | null;
  /** Points that want a value; null: none. */
  dst: Uint8Array | null;
}

/** A plane's edge (half its neighbourhood) has l2/l1 near 0.28, a rod's well under 0.01. */
const smooth = (a: number, b: number, x: number) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };
const gauss = (x: number, s: number) => Math.exp(-0.5 * (x / s) ** 2);

/** A neighbourhood's eigenvalues (any order) as a plane likelihood by shape alone: thin to within tol, and spread both ways. */
export function planeScore(vals: number[], tol: number): number {
  const [l1, l2, l3] = vals.map(v => Math.max(0, v)).sort((a, b) => b - a);
  if (!(l1 > 0)) return NaN;
  return gauss(Math.sqrt(l3), tol) * smooth(0.02, 0.15, l2 / l1);
}

/** How far the neighbours reach (their widest variance against a whole disc of radius r's, r²/4): a disc 1, half or a quarter of one (an edge, a corner) 0.4 to 1, a ball's cap facing one way (deg 20) about 0.1. */
const reach = (l1: number, r: number) => smooth(0.1, 0.3, l1 / (r * r / 4));

/**
 * Per cloud, each dst point's plane likelihood (0 to 1; NaN: not wanted, or too few neighbours within r).
 * tol: how thick a plane's points may lie (m); deg: how far its normals may stray from it (degrees).
 */
export function planarity(clouds: PlanarCloud[], r: number, tol: number, deg = 15): (Float32Array | null)[] {
  // the pool, every cloud's together, a point per cube of r·THIN (the first one in it: its noise stays)
  const cube = (v: number, x: number, y: number, z: number) => ((Math.floor(x / v) + 65536) * 131072 + (Math.floor(y / v) + 65536)) * 131072 + (Math.floor(z / v) + 65536);
  const seen = new Set<number>(), xyz: number[] = [], nrm: number[] = [];
  for (const c of clouds) {
    const m = c.pool, P = c.P, N = c.N;
    if (!m) continue;
    for (let i = 0; i < m.length; i++) {
      if (!m[i]) continue;
      const x = P[3 * i], y = P[3 * i + 1], z = P[3 * i + 2];
      if (!Number.isFinite(x + y + z)) continue;
      const key = cube(r * THIN, x, y, z);
      if (seen.has(key)) continue;
      seen.add(key); xyz.push(x, y, z);
      if (N) nrm.push(N[3 * i], N[3 * i + 1], N[3 * i + 2]); else nrm.push(NaN, NaN, NaN);
    }
  }
  // the pool by cells of r/2, packed in cell order: a point's neighbours are in the 5 × 5 columns of 5 cells round its
  // own, each column one run of the pool (its cells' keys follow on)
  const h = r / 2, np = xyz.length / 3, ckey = new Float64Array(np), ord = Int32Array.from({ length: np }, (_, i) => i);
  for (let i = 0; i < np; i++) ckey[i] = cube(h, xyz[3 * i], xyz[3 * i + 1], xyz[3 * i + 2]);
  ord.sort((a, b) => ckey[a] - ckey[b]);
  const Q = new Float32Array(3 * np), QN = new Float32Array(3 * np), K = new Float64Array(np);
  for (let s = 0; s < np; s++) {
    const i = ord[s];
    Q[3 * s] = xyz[3 * i]; Q[3 * s + 1] = xyz[3 * i + 1]; Q[3 * s + 2] = xyz[3 * i + 2];
    QN[3 * s] = nrm[3 * i]; QN[3 * s + 1] = nrm[3 * i + 1]; QN[3 * s + 2] = nrm[3 * i + 2];
    K[s] = ckey[i];
  }
  /** The first pool point whose cell key is k or more. */
  const from = (k: number) => { let lo = 0, hi = np; while (lo < hi) { const m = (lo + hi) >> 1; if (K[m] < k) lo = m + 1; else hi = m; } return lo; };
  const ang = (deg * Math.PI) / 180, cosG = Math.cos(ang), r2 = r * r, A = [0, 0, 0, 0, 0, 0, 0, 0, 0];
  const spans: number[] = [], inner: number[] = [];
  let at = NaN;
  /** The runs of the pool in the 125 cells round point (x, y, z)'s cell, and in the 27 nearest (kept while points stay in one cell). */
  const near = (x: number, y: number, z: number) => {
    const cx = Math.floor(x / h), cy = Math.floor(y / h), cz = Math.floor(z / h), k = cube(h, x, y, z);
    if (k === at) return;
    at = k; spans.length = inner.length = 0;
    for (let i = -2; i <= 2; i++) for (let j = -2; j <= 2; j++) {
      const col = ((cx + i + 65536) * 131072 + (cy + j + 65536)) * 131072 + cz + 65536;
      const a = from(col - 2), b = from(col + 3);
      if (a === b) continue;
      spans.push(a, b);
      if (Math.abs(i) <= 1 && Math.abs(j) <= 1) inner.push(from(col - 1), from(col + 2));
    }
  };
  let within = new Int32Array(1024);   // a fit's neighbours within r (pool indices), for its second look
  const F = new Float64Array(6);   // a fit's plane: its centre and normal
  /**
   * The plane through the neighbours of (x0, y0, z0) facing its way (normal o; NaN: every neighbour), into F, and how
   * plane-like they are (NaN: too few). near() first.
   */
  const fit = (x0: number, y0: number, z0: number, ox: number, oy: number, oz: number): number => {
    const own = ox === ox, Qa = Q, QNa = QN, sp = spans, cg2 = cosG * cosG;
    let W = within;
    // the neighbours facing its way, about the point itself (far-off coordinates keep their precision); their normals' tensor
    let n = 0, sx = 0, sy = 0, sz = 0, sxx = 0, sxy = 0, sxz = 0, syy = 0, syz = 0, szz = 0;
    let nn = 0, t00 = 0, t01 = 0, t02 = 0, t11 = 0, t12 = 0, t22 = 0, nw = 0;
    for (let q = 0; q < sp.length; q += 2) for (let s = sp[q], e = sp[q + 1]; s < e; s++) {
      const dx = Qa[3 * s] - x0, dy = Qa[3 * s + 1] - y0, dz = Qa[3 * s + 2] - z0;
      if (dx * dx + dy * dy + dz * dz > r2) continue;
      if (nw === W.length) { const g = new Int32Array(2 * nw); g.set(W); within = W = g; }
      W[nw++] = s;
      const a = QNa[3 * s], b = QNa[3 * s + 1], c = QNa[3 * s + 2];
      if (own) { const d = a * ox + b * oy + c * oz; if (!(d * d >= cg2)) continue; }
      n++; sx += dx; sy += dy; sz += dz;
      sxx += dx * dx; sxy += dx * dy; sxz += dx * dz; syy += dy * dy; syz += dy * dz; szz += dz * dz;
      if (a === a) { nn++; t00 += a * a; t01 += a * b; t02 += a * c; t11 += b * b; t12 += b * c; t22 += c * c; }
    }
    if (n < MIN_PTS) return NaN;
    const mx = sx / n, my = sy / n, mz = sz / n;
    A[0] = sxx / n - mx * mx; A[4] = syy / n - my * my; A[8] = szz / n - mz * mz;
    A[1] = A[3] = sxy / n - mx * my; A[2] = A[6] = sxz / n - mx * mz; A[5] = A[7] = syz / n - my * mz;
    const { vals, vecs } = eigSym3(A), k = vals[0] <= vals[1] ? (vals[0] <= vals[2] ? 0 : 2) : vals[1] <= vals[2] ? 1 : 2;
    const ax = vecs[k], ay = vecs[3 + k], az = vecs[6 + k];
    F[0] = x0 + mx; F[1] = y0 + my; F[2] = z0 + mz; F[3] = ax; F[4] = ay; F[5] = az;
    let v = planeScore(vals, tol) * reach(Math.max(vals[0], vals[1], vals[2]), r);
    if (nn >= MIN_PTS) {   // the neighbours' normals: their rms angle off the plane's (mean cos² of it)
      const c2 = (ax * ax * t00 + ay * ay * t11 + az * az * t22 + 2 * (ax * ay * t01 + ax * az * t02 + ay * az * t12)) / nn;
      v *= gauss(Math.asin(Math.sqrt(Math.min(1, Math.max(0, 1 - c2)))), ang);
    }
    // of every neighbour lying on that plane (to within tol), those facing its way: most on a plane; few in clutter,
    // whose points face every way (a wall's points beside a floor point lie off the floor's plane: they don't count)
    if (own) {
      const w = mx * ax + my * ay + mz * az;
      let on = 0, facing = 0;
      for (let q = 0; q < nw; q++) {
        const s = W[q], a = QNa[3 * s];
        if (a !== a || Math.abs((Qa[3 * s] - x0) * ax + (Qa[3 * s + 1] - y0) * ay + (Qa[3 * s + 2] - z0) * az - w) > tol) continue;
        on++;
        const d = a * ax + QNa[3 * s + 1] * ay + QNa[3 * s + 2] * az;
        if (d * d >= cg2) facing++;
      }
      v *= smooth(0.3, 0.7, on ? facing / on : 0);
    }
    return v;
  };
  // the pool's own fits: per pool point its score (NaN: none) and plane (centre, normal)
  const S = new Float32Array(np), PL = new Float32Array(6 * np);
  for (let s = 0; s < np; s++) {
    near(Q[3 * s], Q[3 * s + 1], Q[3 * s + 2]);
    S[s] = fit(Q[3 * s], Q[3 * s + 1], Q[3 * s + 2], QN[3 * s], QN[3 * s + 1], QN[3 * s + 2]);
    if (S[s] === S[s]) PL.set(F, 6 * s);
  }
  const rho = r * BLEND, rho2 = rho * rho;
  return clouds.map(c => {
    const m = c.dst, P = c.P, N = c.N;
    if (!m) return null;
    const out = new Float32Array(m.length).fill(NaN);
    // a cell at a time (its cells' spans once)
    const idx: number[] = [];
    for (let i = 0; i < m.length; i++) if (m[i] && Number.isFinite(P[3 * i] + P[3 * i + 1] + P[3 * i + 2])) idx.push(i);
    const key = new Float64Array(m.length);
    for (const i of idx) key[i] = cube(h, P[3 * i], P[3 * i + 1], P[3 * i + 2]);
    idx.sort((a, b) => key[a] - key[b]);
    at = NaN;
    const QNa = QN, Qa = Q, sp = inner, cg2 = cosG * cosG;
    for (const i of idx) {
      const x0 = P[3 * i], y0 = P[3 * i + 1], z0 = P[3 * i + 2];
      const ox = N ? N[3 * i] : NaN, oy = N ? N[3 * i + 1] : NaN, oz = N ? N[3 * i + 2] : NaN, own = ox === ox;
      near(x0, y0, z0);
      // the pool's fits within rho facing its way, blended (their normals turned one way: its own, or the first's)
      let ws = 0, vs = 0, cx = 0, cy = 0, cz = 0, nx = 0, ny = 0, nz = 0, rx = ox, ry = oy, rz = oz;
      for (let q = 0; q < sp.length; q += 2) for (let s = sp[q], e = sp[q + 1]; s < e; s++) {
        const v = S[s];
        if (v !== v) continue;
        const dx = Qa[3 * s] - x0, dy = Qa[3 * s + 1] - y0, dz = Qa[3 * s + 2] - z0, d2 = dx * dx + dy * dy + dz * dz;
        if (d2 >= rho2) continue;
        if (own) { const d = QNa[3 * s] * ox + QNa[3 * s + 1] * oy + QNa[3 * s + 2] * oz; if (!(d * d >= cg2)) continue; }
        let a = PL[6 * s + 3], b = PL[6 * s + 4], cc = PL[6 * s + 5];
        if (rx !== rx) { rx = a; ry = b; rz = cc; }
        if (a * rx + b * ry + cc * rz < 0) { a = -a; b = -b; cc = -cc; }
        const t = 1 - d2 / rho2, w = t * t;
        ws += w; vs += w * v;
        cx += w * PL[6 * s]; cy += w * PL[6 * s + 1]; cz += w * PL[6 * s + 2]; nx += w * a; ny += w * b; nz += w * cc;
      }
      let v: number;
      if (ws > 0) {
        const l = Math.hypot(nx, ny, nz) || 1;
        v = vs / ws; F[0] = cx / ws; F[1] = cy / ws; F[2] = cz / ws; F[3] = nx / l; F[4] = ny / l; F[5] = nz / l;
      } else {   // none near facing its way: its own fit
        v = fit(x0, y0, z0, ox, oy, oz);
        if (v !== v) continue;
      }
      // and the point itself on that plane, facing its way (a stray point beside a wall has the wall's neighbourhood)
      v *= gauss((x0 - F[0]) * F[3] + (y0 - F[1]) * F[4] + (z0 - F[2]) * F[5], tol);
      if (own) v *= gauss(Math.acos(Math.min(1, Math.abs(ox * F[3] + oy * F[4] + oz * F[5]))), ang);
      out[i] = v;
    }
    return out;
  });
}
